import React, {useCallback, useEffect, useState} from 'react';
import {ActivityIndicator, FlatList, Image, ScrollView, StyleSheet, View} from 'react-native';
import type {NavigationProp} from '@react-navigation/native';
import {useFocusEffect} from '@react-navigation/native';
import {ThemeProvider} from '@rn-vui/themed';
import {observer} from 'mobx-react-lite';

import {ErrorBanner} from '../../../components/ErrorBanner';
import LoadingImg from '../../../components/LoadingImg';
import {ProductTileCarousel} from '../../../components/ProductTile';
import Button from '../../../components/themed/Button';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import Markdown from '../../../components/ui-controls/Markdown';
import Text from '../../../components/ui-controls/Text';
import {useTabBarBottomPadding} from '../../../hooks/useTabBarBottomPadding';
import {useWebScreenDimensions} from '../../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import {OfferDetailStore} from '../../../stores/offersStore';
import {useStores} from '../../../stores/use-stores';
import theme from '../../../style/theme';
import rewardsTheme from '../../../theme/rewardsTheme';
import {getRecoveryButtonLabel, getRecoveryRoute} from '../../../utils/offerErrorHandler';

import CoffeeCard from './CoffeeCard';
import OfferNotFound from './OfferNotFound';

type ExclusiveOfferDetailScreenStandaloneProps = {
  offerDetails?: OfferDetailStore;
  isLoading?: boolean;
  isNotFound?: boolean;
  navigation: NavigationProp<ReactNavigation.RootParamList>;
};
export const ExclusiveOfferDetailScreenStandalone = observer(
  ({
    offerDetails,
    isLoading,
    isNotFound,
    navigation,
  }: ExclusiveOfferDetailScreenStandaloneProps) => {
    const {cartStore, shopSelectionStore} = useStores();
    const {isWebDesktopScreen} = useWebScreenDimensions();
    const tabBarPadding = useTabBarBottomPadding();
    const isApplied = cartStore.isOfferApplied(offerDetails?.offer.mflId || '');
    const {applyError} = offerDetails || {};

    if (isLoading) {
      return (
        <ContentView testID="exclusive-offer-detail-screen-loading">
          <LoadingImg />
        </ContentView>
      );
    }

    if (isNotFound) {
      return (
        <ContentView testID="exclusive-offer-detail-screen-not-found">
          <OfferNotFound navigation={navigation} />
        </ContentView>
      );
    }

    if (!offerDetails?.offer) {
      return (
        <ContentView testID="exclusive-offer-detail-screen-loading">
          <LoadingImg />
        </ContentView>
      );
    }

    const hasBarcodes = offerDetails.offer.barcodes && offerDetails.offer.barcodes.length > 0;
    const isLoadingProducts = offerDetails.isLoadingLinkedProducts;
    const hasProducts = offerDetails.linkedProducts && offerDetails.linkedProducts.length > 0;
    const hasBarcodesButNoProducts =
      hasBarcodes && offerDetails.linkedProducts !== null && !hasProducts;

    // Simple disabled logic - no client-side eligibility validation (server handles it)
    const isButtonDisabled = offerDetails.isApplyLoading || isApplied || hasBarcodesButNoProducts; // Keep product availability check for UX

    const getRecoveryAction = () => {
      if (!applyError?.recoveryAction || applyError.recoveryAction === 'retry') {
        return null;
      }

      const label = getRecoveryButtonLabel(applyError.recoveryAction);
      const route = getRecoveryRoute(applyError.recoveryAction);

      if (!label || !route) return null;

      return {
        label,
        onPress: () => navigation.navigate(route),
      };
    };

    const image = isWebDesktopScreen
      ? offerDetails?.offer?.heroImageDesktop?.[0]
      : offerDetails?.offer?.heroImage?.[0];

    const setButtonTitle = () => {
      if (hasBarcodesButNoProducts) {
        return 'Offer currently not available';
      }
      return isApplied ? 'APPLIED' : 'APPLY';
    };

    return (
      <ContentView testID="exclusive-offer-detail-screen">
        <ScrollView
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{paddingBottom: tabBarPadding}}>
          {image?.url ? (
            <Image
              source={{uri: image.url}}
              style={{
                width: '100%',
                height: undefined,
                aspectRatio: image.width && image.height ? image.width / image.height : 1.52,
              }}
              resizeMode="cover"
            />
          ) : null}

          <View style={styles.textCard}>
            <Text style={styles.headline}>{offerDetails?.offer?.headline}</Text>
            <Markdown>{offerDetails?.offer?.description}</Markdown>
          </View>

          {offerDetails?.offer?.offerTrigger === 'Coffee Card' && (
            <CoffeeCard offer={offerDetails.offer} />
          )}

          {/* Inline error banner for recoverable errors */}
          {applyError?.recoverable ? (
            <ErrorBanner
              message={applyError.message}
              action={getRecoveryAction()}
              testID="apply-offer-error"
            />
          ) : null}

          <Button
            onPress={async () => {
              await offerDetails.applyOffer();
            }}
            loading={offerDetails.isApplyLoading}
            disabled={isButtonDisabled}
            title={setButtonTitle()}
            buttonStyle={styles.enterHereButton}
          />
          {hasBarcodes && !shopSelectionStore.ecommerceDisabled ? (
            isLoadingProducts ? (
              <ActivityIndicator
                style={styles.linkedProductsActivityIndicator}
                testID="linked-products-loading"
              />
            ) : hasProducts ? (
              <ThemeProvider theme={rewardsTheme}>
                <FlatList
                  data={offerDetails.linkedProducts}
                  keyExtractor={item => String(item.id)}
                  ItemSeparatorComponent={ItemSeparatorComponent}
                  style={styles.flatList}
                  renderItem={({item}) => {
                    return (
                      <ProductTileCarousel
                        key={item.id}
                        item={item}
                        testContext="exclusive-offer"
                      />
                    );
                  }}
                  horizontal
                />
              </ThemeProvider>
            ) : (
              <View style={styles.textCard}>
                <Text>
                  The product(s) associated to this offer are currently not available in this store.
                </Text>
              </View>
            )
          ) : null}
        </ScrollView>
      </ContentView>
    );
  },
);

export type ExclusiveOfferDetailScreenProps =
  RouteNavigatorScreenProps<AppRoute.EXCLUSIVE_OFFER_DETAIL> & {
    onOnlineOrderAfterSubmit?: () => void;
    onOnlineOrderCancel?: () => void;
    isLastCompetitionRemaining: boolean; // Bundy Power Plays
  };

const ExclusiveOfferDetailScreen = observer(
  ({route, navigation}: ExclusiveOfferDetailScreenProps) => {
    const rootStore = useStores();
    const {offersStore, userStore} = rootStore;
    const isLoggedIn = userStore.isLoggedIn;

    const [offerDetails, setOfferDetails] = useState<OfferDetailStore>();

    // Refresh offers on screen focus so canApply/disabledReason stay in sync with server
    useFocusEffect(
      useCallback(() => {
        offersStore.refreshOffers();
      }, [offersStore]),
    );

    const offer = offersStore.data?.find(({mflId}) => mflId === route.params.mflId);

    // Loading state: data not fetched yet OR currently fetching OR offer found but details not set
    const isDataNotFetched = offersStore.data === null;
    const isCurrentlyLoading = offersStore.isLoading;
    const offerFoundButDetailsNotSet = !!offer && !offerDetails;
    const isLoading = isDataNotFetched || isCurrentlyLoading || offerFoundButDetailsNotSet;

    // Not found: data has been fetched AND offer doesn't exist in the list
    const isNotFound = offersStore.data !== null && !offersStore.isLoading && !offer;

    useEffect(() => {
      if (offer) {
        setOfferDetails(new OfferDetailStore(offer, rootStore));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offer?.mflId]);

    // Handle deep linking "404". TODO: Better way? In "linking.ts"?
    useEffect(() => {
      if (!route.params?.mflId) {
        navigation.navigate(AppRoute.HOME);
      }
    }, [navigation, route.params]);

    useEffect(() => {
      if (!isLoggedIn) {
        navigation.navigate(AppRoute.HOME);
      }
    }, [isLoggedIn, navigation]);

    return (
      <ExclusiveOfferDetailScreenStandalone
        isLoading={isLoading}
        isNotFound={isNotFound}
        offerDetails={offerDetails}
        navigation={navigation}
      />
    );
  },
);

function ItemSeparatorComponent() {
  return <View style={styles.separator} />;
}

export default ExclusiveOfferDetailScreen;

const styles = StyleSheet.create({
  textCard: {
    marginTop: 24,
  },
  termsAndConditions: {
    marginTop: 16,
    alignItems: 'center',
  },
  termsAndConditionsLink: {
    fontSize: 14,
    color: theme.colour.text.link.primary,
  },
  enterText: {
    fontSize: 16,
    color: 'white',
  },
  headline: {
    fontSize: 20,
    color: theme.colour.text.heading.primary,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  flatList: {
    marginTop: 32,
    marginBottom: 4,
  },
  enterHereButton: {
    width: '80%',
  },
  copy: {
    fontSize: 16,
  },
  linkedProductsActivityIndicator: {
    marginVertical: 16,
  },
  separator: {
    width: 8,
    height: 8,
  },
});
